import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ExternalLink, Heart, Send, Loader2, CheckCircle2 } from "lucide-react";
import { SITE_INFO, SEDI } from "@/data/siteData";
import { CLOAKED_MODE, isPathAllowed } from "@/config/cloakedMode";
import { Checkbox } from "@/components/ui/checkbox";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function NewsletterBlock() {
  const [email, setEmail] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError("Inserisci un indirizzo email valido.");
      return;
    }
    if (!privacy) {
      setError("Devi accettare la privacy policy per iscriverti.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), privacy_accettata: privacy }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSuccess(true);
    } catch (err) {
      setError("Si è verificato un errore nell'invio. Riprova più tardi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="flex items-center gap-2 text-sm text-white/90" data-testid="newsletter-success">
        <CheckCircle2 size={18} className="text-emerald-400 shrink-0" aria-hidden="true" />
        <span>Grazie! Iscrizione alla newsletter completata.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-testid="newsletter-form" aria-label="Iscrizione alla newsletter">
      <h3 className="font-semibold text-sm uppercase tracking-wider mb-3 font-['Encode_Sans_Semi_Condensed']">
        Iscriviti alla newsletter
      </h3>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="La tua email"
          aria-label="Indirizzo email"
          data-testid="newsletter-email-input"
          className="flex-1 min-w-0 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40 transition-colors"
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid="newsletter-submit-button"
          className="shrink-0 inline-flex items-center justify-center gap-1.5 bg-white text-[var(--confcom-blue)] text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg hover:bg-white/90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
          Iscriviti
        </button>
      </div>
      <label className="flex items-start gap-2 mt-3 cursor-pointer group" data-testid="newsletter-privacy-label">
        <Checkbox
          checked={privacy}
          onCheckedChange={(v) => setPrivacy(v === true)}
          data-testid="newsletter-privacy-checkbox"
          className="mt-0.5 border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-[var(--confcom-blue)] data-[state=checked]:border-white"
        />
        <span className="text-xs text-white/60 group-hover:text-white/80 transition-colors leading-relaxed">
          Ho letto e accetto la <Link to="/privacy" className="underline hover:text-white">privacy policy</Link>
        </span>
      </label>
      {error && (
        <p className="text-xs text-red-300 mt-2" role="alert" data-testid="newsletter-error">{error}</p>
      )}
    </form>
  );
}

export default function Footer() {
  const filterLinks = (links) =>
    CLOAKED_MODE ? links.filter((l) => isPathAllowed(l.path.split("#")[0])) : links;

  const associazioneLinks = filterLinks([
    { label: "Chi Siamo", path: "/chi-siamo" },
    { label: "Servizi", path: "/servizi" },
    { label: "Associazioni", path: "/associazioni" },
    { label: "Convenzioni", path: "/convenzioni" },
    { label: "Trasparenza", path: "/chi-siamo/trasparenza" },
  ]);

  const informazioneLinks = filterLinks([
    { label: "Notizie", path: "/notizie" },
    { label: "Appuntamenti", path: "/eventi" },
    { label: "Contatti", path: "/contatti" },
    // { label: "Area Riservata", path: "/area-riservata" }, // bozza: oscurato dal frontend, ripubblicare rimuovendo il commento
  ]);

  return (
    <footer className="bg-[var(--confcom-blue-deeper)] text-white" role="contentinfo" aria-label="Footer del sito">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-[1.8rem]">
          {/* Brand */}
          <div>
            <div className="mb-6">
              <img
                src="/logo-confcom-roma-white.svg"
                alt="Confcommercio Roma"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {SITE_INFO.tagline}
            </p>
            <div className="space-y-3 text-sm">
              <a href={`tel:${SITE_INFO.phone}`} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="footer-phone">
                <Phone size={16} aria-hidden="true" />
                <span>{SITE_INFO.phone}</span>
              </a>
              <a href={`mailto:${SITE_INFO.email}`} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors" data-testid="footer-email">
                <Mail size={16} aria-hidden="true" />
                <span>{SITE_INFO.email}</span>
              </a>
              <div className="flex items-start gap-2 text-white/80">
                <MapPin size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                <span>{SITE_INFO.address}</span>
              </div>
            </div>

            {/* Social icons */}
            <div className="flex items-center gap-3 mt-6" data-testid="footer-social">
              <a href="https://www.facebook.com/ConfcommercioRoma" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" aria-label="Facebook" data-testid="social-facebook">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/confcommercioroma/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" aria-label="Instagram" data-testid="social-instagram">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-white"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.linkedin.com/company/confcommercio-roma" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" aria-label="LinkedIn" data-testid="social-linkedin">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://www.youtube.com/@ConfRomaTube" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors" aria-label="YouTube" data-testid="social-youtube">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="text-white"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
            </div>
          </div>

          {/* Newsletter + Links (Associazione / Informazione) */}
          <div className="md:col-span-2 lg:col-span-2 lg:pl-8">
            <div className="mb-12 max-w-sm">
              <NewsletterBlock />
            </div>
            <div className="grid grid-cols-2 gap-[1.8rem]">
              {associazioneLinks.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm uppercase tracking-wider mb-6 font-['Encode_Sans_Semi_Condensed']">L'Associazione</h3>
                <ul className="space-y-3" role="list">
                  {associazioneLinks.map((link) => (
                    <li key={link.path}>
                      <Link to={link.path} className="text-sm text-white/70 hover:text-white transition-colors" data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              )}

              {informazioneLinks.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm uppercase tracking-wider mb-6 font-['Encode_Sans_Semi_Condensed']">Informazione</h3>
                <ul className="space-y-3" role="list">
                  {informazioneLinks.map((link) => (
                    <li key={link.path}>
                      <Link to={link.path} className="text-sm text-white/70 hover:text-white transition-colors">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              )}
            </div>
          </div>

          {/* Sedi + Promo.Ter, con gap interno indipendente (riducibile senza toccare gli altri gap) */}
          <div className="lg:col-span-2">
            <div className="grid grid-cols-2 gap-x-4 gap-y-[1.8rem]">
              {/* Sedi */}
              <div>
                <h3 className="font-semibold text-sm uppercase tracking-wider mb-6 font-['Encode_Sans_Semi_Condensed']">Le nostre sedi</h3>
                <div className="space-y-6">
                  {SEDI.map((sede) => (
                    <div key={sede.nome} className="text-sm pb-5 last:pb-0">
                      <div className="font-semibold text-white/90 mb-1">{sede.nome}</div>
                      <div className="text-white/60 whitespace-pre-line">{sede.indirizzo}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Promo.Ter */}
              <div>
                <h3 className="font-semibold text-sm uppercase tracking-wider mb-6 font-['Encode_Sans_Semi_Condensed']">Promo.Ter</h3>
                <div className="space-y-3 text-sm text-white/70">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="mt-0.5 shrink-0 text-white/80" aria-hidden="true" />
                    <span>Sede Legale e Operativa<br />Via Marco e Marcelliano 45<br />00147 Roma</span>
                  </div>
                  <a href="tel:+390668437606" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone size={14} className="text-white/80 shrink-0" aria-hidden="true" />
                    <span>Tel: 06 68437606</span>
                  </a>
                  <a href="mailto:promoter@confcommercioroma.it" className="flex items-center gap-2 hover:text-white transition-colors break-all">
                    <Mail size={14} className="text-white/80 shrink-0" aria-hidden="true" />
                    <span>promoter@confcommercioroma.it</span>
                  </a>
                </div>

                {/* C.A.T. — stesso schema, sotto Promo.Ter */}
                <h3 className="font-semibold text-sm uppercase tracking-wider mt-8 mb-6 font-['Encode_Sans_Semi_Condensed']">C.A.T.</h3>
                <div className="space-y-3 text-sm text-white/70">
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="mt-0.5 shrink-0 text-white/80" aria-hidden="true" />
                    <span>Sede Legale e Operativa<br />Via Marco e Marcelliano 45<br />00147 Roma</span>
                  </div>
                  <a href="tel:+390668437369" className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone size={14} className="text-white/80 shrink-0" aria-hidden="true" />
                    <span>Tel: 06.68437369</span>
                  </a>
                  <a href="mailto:servizi-cat@confcommercioroma.it" className="flex items-center gap-2 hover:text-white transition-colors break-all">
                    <Mail size={14} className="text-white/80 shrink-0" aria-hidden="true" />
                    <span>servizi-cat@confcommercioroma.it</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/50">
            &copy; {new Date().getFullYear()} Confcommercio Roma. Tutti i diritti riservati. P.IVA 01234567890
          </p>
          <div className="flex items-center gap-6 text-xs text-white/50">
            <Link to="/privacy" className="hover:text-white/80 transition-colors">Privacy Policy</Link>
            <Link to="/cookie" className="hover:text-white/80 transition-colors">Cookie Policy</Link>
            <span className="flex items-center gap-1">
              Design with <Heart size={11} className="text-red-500 fill-red-500" /> by{" "}
              <a href="https://toyou.it/" target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition-colors">TO<strong className="font-bold text-white/70">YOU</strong></a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
